import { useState } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { useApiKey } from '@/stores/apiKey.store';

interface Props {
    endpointContext: string;
    operationId: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.satutim.id';

export default function AskAiBar({ endpointContext, operationId }: Props) {
    const { key } = useApiKey();
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(false);
    const [answer, setAnswer] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        if (!q.trim() || busy) return;
        if (!key) {
            setError('Aktifkan API key dulu di satutim.id/settings/api');
            return;
        }
        setBusy(true);
        setError(null);
        setAnswer('');
        try {
            const res = await fetch(`${API_BASE}/api/v1/ai/playground-ask`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${key}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ operationId, context: endpointContext, question: q.trim() }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(body.error || `HTTP ${res.status}`);
                return;
            }
            const data = await res.json() as { answer?: string };
            setAnswer(data.answer || '(jawaban kosong)');
        } catch (e: any) {
            setError(e?.message || 'Network error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-ink">Tanya AI</span>
                <span className="text-xxs text-ghost">— jawab kontekstual dengan spec endpoint ini</span>
            </div>
            <div className="flex items-center gap-2">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
                    placeholder="Ajukan pertanyaan… (⌘↵ untuk submit)"
                    className="flex-1 h-10 px-3 rounded-md text-sm text-ink placeholder:text-ghost outline-none"
                    style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                    disabled={busy}
                />
                <button
                    onClick={submit}
                    disabled={!q.trim() || busy}
                    className="h-10 px-3 rounded-md flex items-center justify-center transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: 'var(--accent)', color: 'var(--bg)' }}
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
            </div>
            {error && (
                <p className="mt-2 text-xs px-3 py-2 rounded-md" style={{ color: 'var(--err)', backgroundColor: 'var(--err)11', border: '1px solid var(--err)33' }}>
                    {error}
                </p>
            )}
            {answer && (
                <div className="mt-3 p-3 rounded-md text-sm leading-relaxed whitespace-pre-wrap" style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                    {answer}
                </div>
            )}
        </div>
    );
}
